import React, { useRef, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    CircularProgress,
    Grid,
    InputAdornment,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNewToken } from './useNewToken';
import queueTicketService from '../../services/queueTicketService';
import LanguageSwitcher from '../../common/LanguageSwitcher/LanguageSwitcher';
import Constants from '../../common/constants';

function normalizeKzPhone(value) {
    const digits = String(value || '').replace(/\D/g, '');
    if (!digits) return '';
    if (digits.length === 10) return '7' + digits;
    if (digits.length === 11 && digits[0] === '8') return '7' + digits.slice(1);
    return digits;
}

function formatPretty(value) {
    const d = String(value || '').replace(/\D/g, '');
    if (d.length < 10) return value;
    const last10 = d.slice(-10);
    return `+7 ${last10.slice(0, 3)} ${last10.slice(3, 6)}-${last10.slice(6, 8)}-${last10.slice(8, 10)}`;
}

function getLocalizedName(serviceType, language) {
    if (language === 'kk' || language === 'kz') return serviceType.nameKk || serviceType.nameRu;
    if (language === 'en') return serviceType.nameEn || serviceType.nameRu;
    return serviceType.nameRu;
}

function GetNewToken() {
    const { t, i18n } = useTranslation();
    const { serviceTypes, loading: stLoading, errorMessage: stError } = useNewToken();
    const navigate = useNavigate();
    const captchaRef = useRef(null);

    const [fullName, setFullName] = useState('');
    const [phoneRaw, setPhoneRaw] = useState('');
    const [selectedServiceTypeId, setSelectedServiceTypeId] = useState(null);
    const [captchaDone, setCaptchaDone] = useState(!Constants.RECAPTCHA_SITE_KEY);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const digits = normalizeKzPhone(phoneRaw);
    const isPhoneValid = digits.length >= 11;

    const canSubmit = useMemo(
        () => !!fullName.trim() && isPhoneValid && !!selectedServiceTypeId && captchaDone && !submitting && !stLoading,
        [fullName, isPhoneValid, selectedServiceTypeId, captchaDone, submitting, stLoading]
    );

    const onSubmit = async e => {
        e?.preventDefault?.();
        if (!canSubmit) return;
        setSubmitting(true);
        setSubmitError('');
        try {
            const selectedSt = serviceTypes.find(st => st.id === selectedServiceTypeId);
            const captchaToken = captchaRef.current?.getValue() || null;
            const res = await queueTicketService.generateTicket({
                phoneNumber: normalizeKzPhone(phoneRaw),
                serviceTypeId: selectedServiceTypeId,
                fullName: fullName.trim(),
                program: selectedSt ? getLocalizedName(selectedSt, i18n.language) : '',
                captchaToken
            });
            captchaRef.current?.reset();
            setCaptchaDone(false);
            const ticketNum = res?.data?.ticketNumber;
            const token = res?.data?.trackingToken;
            if (ticketNum && token) {
                sessionStorage.setItem(`ticket_token_${ticketNum}`, token);
            }
            navigate('/my-token', {
                state: {
                    ticketNumber: ticketNum,
                    trackingToken: token,
                    fullName: fullName.trim(),
                    program: selectedSt ? getLocalizedName(selectedSt, i18n.language) : ''
                }
            });
        } catch (err) {
            const msg = err?.response?.data?.message || t('newToken.errorDefault');
            setSubmitError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PageRoot>
            <BgCircle sx={{ width: 320, height: 320, top: -100, right: -80, opacity: 0.12 }} />
            <BgCircle sx={{ width: 200, height: 200, bottom: 40, left: -60, opacity: 0.08 }} />

            <Content>
                <Stack alignItems='center' spacing={1} sx={{ mb: 3.5 }}>
                    <Logo src='/logo-white.png' alt='Dulaty University' />
                    <Typography sx={{ fontWeight: 900, fontSize: 22, color: '#fff', lineHeight: 1.2, textAlign: 'center' }}>
                        {t('tv.university')}
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.65)', textAlign: 'center' }}>
                        {t('newToken.universitySubtitle')}
                    </Typography>
                    <LanguageSwitcher variant='dark' />
                </Stack>

                <FormCard component='form' onSubmit={onSubmit}>
                    <Stack spacing={2.5}>
                        <Stack spacing={0.5}>
                            <Typography sx={{ fontWeight: 800, fontSize: 20 }}>
                                {t('newToken.formTitle')}
                            </Typography>
                            <Typography variant='body2' color='text.secondary' sx={{ fontSize: 14 }}>
                                {t('newToken.formSubtitle')}
                            </Typography>
                        </Stack>

                        {stError && <Alert severity='error' sx={{ borderRadius: 2 }}>{stError}</Alert>}
                        {submitError && <Alert severity='error' sx={{ borderRadius: 2 }}>{submitError}</Alert>}

                        <TextField
                            label={t('newToken.fieldFullName')}
                            placeholder={t('newToken.fieldFullNamePlaceholder')}
                            value={fullName}
                            onChange={e => setFullName(e.target.value)}
                            fullWidth
                            required
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <BadgeRoundedIcon color='primary' fontSize='small' />
                                    </InputAdornment>
                                )
                            }}
                            sx={fieldSx}
                        />

                        <TextField
                            label={t('newToken.fieldPhone')}
                            placeholder='+7 700 000-00-00'
                            value={phoneRaw}
                            onChange={e => setPhoneRaw(e.target.value)}
                            fullWidth
                            required
                            inputMode='tel'
                            error={!!phoneRaw && !isPhoneValid}
                            helperText={
                                phoneRaw
                                    ? isPhoneValid ? formatPretty(phoneRaw) : t('newToken.fieldPhoneError')
                                    : ' '
                            }
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <PhoneIphoneRoundedIcon color='primary' fontSize='small' />
                                    </InputAdornment>
                                )
                            }}
                            sx={fieldSx}
                        />

                        {/* Service type card grid */}
                        <Box>
                            <Typography variant='body2' sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary' }}>
                                {t('newToken.serviceTypeLabel')}
                            </Typography>

                            {stLoading ? (
                                <Box sx={{ textAlign: 'center', py: 2 }}>
                                    <CircularProgress size={28} />
                                </Box>
                            ) : (
                                <Grid container spacing={1.5}>
                                    {serviceTypes.map(st => {
                                        const isSelected = selectedServiceTypeId === st.id;
                                        return (
                                            <Grid item xs={6} key={st.id}>
                                                <Card
                                                    elevation={0}
                                                    sx={{
                                                        backgroundColor: st.color || '#EDE9FE',
                                                        border: isSelected
                                                            ? '2.5px solid #1e40af'
                                                            : '2px solid transparent',
                                                        borderRadius: 3,
                                                        transition: 'border 0.15s, transform 0.1s',
                                                        transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                                                        position: 'relative'
                                                    }}
                                                >
                                                    <CardActionArea
                                                        onClick={() => setSelectedServiceTypeId(st.id)}
                                                        sx={{ borderRadius: 3 }}
                                                    >
                                                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                            <Typography
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    fontSize: 13,
                                                                    lineHeight: 1.3,
                                                                    color: '#1e293b'
                                                                }}
                                                            >
                                                                {getLocalizedName(st, i18n.language)}
                                                            </Typography>
                                                        </CardContent>
                                                    </CardActionArea>
                                                    {isSelected && (
                                                        <CheckCircleRoundedIcon
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 6,
                                                                right: 6,
                                                                fontSize: 18,
                                                                color: '#1e40af'
                                                            }}
                                                        />
                                                    )}
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            )}
                        </Box>

                        {Constants.RECAPTCHA_SITE_KEY && (
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <ReCAPTCHA
                                    ref={captchaRef}
                                    sitekey={Constants.RECAPTCHA_SITE_KEY}
                                    onChange={token => setCaptchaDone(!!token)}
                                    onExpired={() => setCaptchaDone(false)}
                                    hl={i18n.language === 'kk' ? 'ru' : i18n.language}
                                />
                            </Box>
                        )}

                        <SubmitButton
                            type='submit'
                            variant='contained'
                            size='large'
                            disabled={!canSubmit}
                            fullWidth
                            sx={{
                                background: canSubmit ? '#22c55e' : undefined,
                                '&:hover': { background: '#16a34a' }
                            }}
                        >
                            {submitting ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1.5, color: 'inherit' }} />
                                    {t('newToken.submitting')}
                                </>
                            ) : (
                                t('newToken.submit')
                            )}
                        </SubmitButton>
                    </Stack>
                </FormCard>

                <Typography sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.55)', fontSize: 13, mt: 2 }}>
                    {t('newToken.footerNote')}
                </Typography>
            </Content>
        </PageRoot>
    );
}

const fieldSx = {
    '& .MuiOutlinedInput-root': { borderRadius: 3, fontSize: 16, '& fieldset': { borderWidth: 1.5 } },
    '& .MuiInputLabel-root': { fontSize: 15 }
};

const PageRoot = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
    background: 'linear-gradient(150deg, #1e3a8a 0%, #1e40af 60%, #3b82f6 100%)',
    position: 'relative',
    overflow: 'hidden'
}));

const Content = styled(Box)(() => ({
    width: '100%',
    maxWidth: 480,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    position: 'relative',
    zIndex: 1
}));

const FormCard = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: 24,
    padding: theme.spacing(3),
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)'
}));

const Logo = styled('img')(() => ({
    width: 72, height: 72, objectFit: 'contain',
    borderRadius: 16
}));

const BgCircle = styled(Box)(() => ({
    position: 'absolute', borderRadius: '50%',
    backgroundColor: '#fff', pointerEvents: 'none'
}));

const SubmitButton = styled(Button)(() => ({
    height: 52, borderRadius: 14, fontSize: 16,
    fontWeight: 700, textTransform: 'none', boxShadow: 'none',
    '&:hover': { boxShadow: 'none' }
}));

export default GetNewToken;
